// src/utils/share-content.ts
import Bowser from 'bowser';
import copy from './copy-to-clipboard';

export interface ShareContent {
  title?: string;
  text?: string;
  url: string;
}

type ShareResult = 'shared' | 'copied' | 'mailto' | 'failed';

const isMobile = (): boolean => {
  const parser = Bowser.getParser(window.navigator.userAgent);
  return parser.getPlatform().type === 'mobile';
};

/** Opens the user's default mail client with a pre-filled message. */
const shareViaMail = (params: { content: ShareContent }): void => {
  const { content } = params;
  const subject = content?.title || '';
  const body = [content?.text || '', '', content.url, ''].join('\r\n');
  window.location.href = `mailto:?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
};

/** Shares via the Web Share API, falling back to clipboard if unavailable. */
const shareViaNative = async (params: {
  content: ShareContent;
}): Promise<ShareResult> => {
  const { content } = params;
  if (navigator.share) {
    try {
      await navigator.share(content);
      return 'shared';
    } catch (error) {
      // AbortError = user dismissed the share sheet; not a real failure
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
      return 'failed';
    }
  } else {
    const text = [content.title, content.text, '', content.url]
      .filter((line) => line !== undefined)
      .join('\n');
    return copy(text) ? 'copied' : 'failed';
  }
};

/**
 * Shares content using the best method for the device: native share on mobile
 * (with clipboard fallback), mail client on desktop.
 */
export const shareContent = async (params: {
  content: ShareContent;
}): Promise<ShareResult> => {
  const { content } = params;
  if (isMobile()) {
    return shareViaNative({ content });
  } else {
    shareViaMail({ content });
    return 'mailto';
  }
};
